import TeacherPageHeader from '../../components/TeacherPageHeader';
import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { ko, enUS } from 'date-fns/locale';
import { modificationAPI } from '../../services/modificationAPI';
import { LessonScheduleModification } from '../../types/api';
import { calendarAPI, holidayAPI } from '../../services';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../contexts/LanguageContext';
import CustomSelect from '../../components/CustomSelect';
import Tabs from '../../components/Tabs';
import { TIME_SLOTS } from '../../config/constants';

interface Teacher {
  name: string;
}

interface Schedule {
  id?: number;
  scheduleId?: string;
  teacher?: Teacher;
  lessonDate: string;
  startTime: string;
  endTime: string;
  notes?: string;
}

interface UserScheduleInfo {
  dayOfWeek: string;
  startTime: string;
  endTime: string;
}

const UserLessonSchedule: React.FC = () => {
  const { userInfo } = useAuth();
  const { t, language } = useLanguage();
  const dateLocale = language === 'ENG' ? enUS : ko;
  const [changeMode, setChangeMode] = useState<'weekly' | 'permanent'>('weekly'); // 탭 상태
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [requestReason, setRequestReason] = useState('');
  const [requestedTime, setRequestedTime] = useState('11:00');
  const [submitting, setSubmitting] = useState(false);

  // 신청 내역 (시안: 우측 패널)
  const [myRequests, setMyRequests] = useState<LessonScheduleModification[]>([]);
  const loadMyRequests = useCallback(async () => {
    try {
      const res = await modificationAPI.getMyModificationRequests({ limit: 10 });
      if (res.success && res.data) {
        const data = res.data as any;
        setMyRequests(data.requests || data.modifications || data.items || []);
      }
    } catch {
      // 내역 로드 실패 무시
    }
  }, []);

  useEffect(() => {
    loadMyRequests();
  }, [loadMyRequests]);
  const [holidayError, setHolidayError] = useState<string | null>(null);

  // 정기 일정 변경용 상태
  const [newWeekDay, setNewWeekDay] = useState('monday');
  const [newStartTime, setNewStartTime] = useState('11:00');
  const [teacherBusyTimes, setTeacherBusyTimes] = useState<Array<{
    startTime: string;
    endTime: string;
    studentName: string;
    type: string;
  }>>([]);
  // 정기 일정 변경용 바쁜 시간
  const [permanentBusyTimes, setPermanentBusyTimes] = useState<Array<{
    startTime: string;
    endTime: string;
    studentName: string;
    type: string;
  }>>([]);
  const [userOverrides, setUserOverrides] = useState<Array<{
    startDate: string;
    endDate: string;
    newDate: string;
    scope: string;
    type: string;
    newStartTime: string;
    newEndTime: string;
  }>>([]);
  // 임시 데이터 (실제로는 API에서 가져올 데이터)
  const [userScheduleInfo, setUserScheduleInfo] = useState<UserScheduleInfo | null>({
    dayOfWeek: 'monday',
    startTime: '10:00',
    endTime: '11:00'
  });
  const [selectedSchedule] = useState<Schedule | null>(null);
  const [selectedWeekDay, setSelectedWeekDay] = useState('monday');
  const [didInitFromUser, setDidInitFromUser] = useState(false);

  const getWeekdayFromDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const map = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return map[d.getDay()];
  };

  const getDayLabel = (day: string) => {
    const dayMap: { [key: string]: string } = {
      'sunday': t('sunday'),
      'monday': t('monday'),
      'tuesday': t('tuesday'),
      'wednesday': t('wednesday'),
      'thursday': t('thursday'),
      'friday': t('friday'),
      'saturday': t('saturday')
    };
    return dayMap[day] || '';
  };

  // 시간 선택 핸들러 (단일 선택)
  const handleTimeSelect = (time: string) => {
    // 이미 선택된 시간을 다시 클릭하면 해제, 아니면 새 시간으로 변경
    setRequestedTime(requestedTime === time ? '' : time);
  };

  // 정기 일정 시간 선택 핸들러 (단일 선택)
  const handlePermanentTimeSelect = (time: string) => {
    setNewStartTime(newStartTime === time ? '' : time);
  };

  // 시간이 바쁜 시간대인지 확인 (1시간 블록 전체가 비어있어야 선택 가능)
  const isTimeSlotBusy = (time: string, busyTimesList: Array<{ startTime: string; endTime: string }>) => {
    const [hour, minute] = time.split(':').map(Number);
    const timeInMinutes = hour * 60 + minute;
    const slotEndMinutes = timeInMinutes + 60; // 1시간 수업

    return busyTimesList.some(busyTime => {
      const [startHour, startMin] = busyTime.startTime.split(':').map(Number);
      const [endHour, endMin] = busyTime.endTime.split(':').map(Number);
      const startInMinutes = startHour * 60 + (startMin || 0);
      const endInMinutes = endHour * 60 + (endMin || 0);
      // 1시간 블록이 바쁜 시간과 겹치는지 확인
      return timeInMinutes < endInMinutes && slotEndMinutes > startInMinutes;
    });
  };

  // 마지막 슬롯인지 확인 (21:30, 22:00은 선택 불가)
  const isLastSlot = (time: string): boolean => {
    const [hour, minute] = time.split(':').map(Number);
    const timeMinutes = hour * 60 + minute;
    return timeMinutes >= 21 * 60 + 30;
  };

  // 다음 30분이 비어있어서 합칠 수 있는지 확인
  const canMergeWithNext = (time: string, busyTimesList: Array<{ startTime: string; endTime: string }>) => {
    const [hour, minute] = time.split(':').map(Number);
    const nextMinutes = hour * 60 + minute + 30;
    const nextTimeMinutes = Math.floor(nextMinutes / 60) * 60 + (nextMinutes % 60);
    // 다음 30분이 바쁘지 않으면 1시간 수업 가능
    return !busyTimesList.some(busyTime => {
      const [startHour, startMin] = busyTime.startTime.split(':').map(Number);
      const [endHour, endMin] = busyTime.endTime.split(':').map(Number);
      const startInMinutes = startHour * 60 + (startMin || 0);
      const endInMinutes = endHour * 60 + (endMin || 0);
      return nextTimeMinutes >= startInMinutes && nextTimeMinutes < endInMinutes;
    });
  };

  // 정기 일정 변경용 바쁜 시간 조회 (요일 기반)
  const fetchPermanentBusyTimes = async (weekDay: string) => {
    try {
      const teacherId = 1;

      // 선택된 요일의 다음 날짜 계산
      const today = new Date();
      const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(weekDay);
      const todayIndex = today.getDay();
      let daysUntil = dayIndex - todayIndex;
      if (daysUntil <= 0) daysUntil += 7;

      const nextDate = new Date(today);
      nextDate.setDate(today.getDate() + daysUntil);
      const dateString = format(nextDate, 'yyyy-MM-dd');

      const response = await calendarAPI.getUnifiedCalendar({
        startDate: dateString,
        endDate: dateString,
        teacherId: teacherId,
        includeRegular: true
      });

      const eventsByDate = response.data.data.events || [];
      const busyTimes: Array<{
        startTime: string;
        endTime: string;
        studentName: string;
        type: string;
      }> = [];

      eventsByDate.forEach((dayEvent: any) => {
        if (dayEvent.schedules && Array.isArray(dayEvent.schedules)) {
          dayEvent.schedules.forEach((schedule: any) => {
            if (userInfo && 'id' in userInfo && schedule.userId === (userInfo as any).id) {
              return;
            }
            busyTimes.push({
              startTime: schedule.startTime || schedule.lessonStartTime,
              endTime: schedule.endTime || schedule.lessonEndTime,
              studentName: schedule.studentName || schedule.userName || t('busy_time_label'),
              type: schedule.scheduleType || 'regular'
            });
          });
        } else if (dayEvent.startTime && dayEvent.endTime) {
          if (userInfo && 'id' in userInfo && dayEvent.userId === (userInfo as any).id) {
            return;
          }
          busyTimes.push({
            startTime: dayEvent.startTime,
            endTime: dayEvent.endTime,
            studentName: dayEvent.studentName || dayEvent.userName || t('busy_time_label'),
            type: dayEvent.scheduleType || 'regular'
          });
        }
      });

      setPermanentBusyTimes(busyTimes);
    } catch {
      setPermanentBusyTimes([]);
    }
  };

  // 선생님 바쁜 시간 조회
  const fetchTeacherBusyTimes = async () => {
    try {
      // customEndDate가 없으면 함수 종료 (날짜 지정 모드가 아님)
      if (!customEndDate) {
        setTeacherBusyTimes([]);
        return;
      }

      // teacherId 확인 - teacher가 한명이므로 1 고정
      const teacherId = 1;

      // 날짜 지정 모드일 경우 단일 날짜 바쁨 우선 조회
      try {
        const response = await calendarAPI.getUnifiedCalendar({
          startDate: customEndDate,
          endDate: customEndDate,
          teacherId: teacherId,
          includeRegular: true
        });

        const eventsByDate = response.data.data.events || [];
        const busyTimes: Array<{
          startTime: string;
          endTime: string;
          studentName: string;
          type: string;
        }> = [];

        // API 응답 구조에 맞게 파싱 (dayEvent > schedules 구조)
        eventsByDate.forEach((dayEvent: any) => {
          if (dayEvent.schedules && Array.isArray(dayEvent.schedules)) {
            dayEvent.schedules.forEach((schedule: any) => {
              // 현재 사용자의 일정은 제외 (자신의 일정은 busy가 아님)
              if (userInfo && 'id' in userInfo && schedule.userId === (userInfo as any).id) {
                return;
              }
              busyTimes.push({
                startTime: schedule.startTime || schedule.lessonStartTime,
                endTime: schedule.endTime || schedule.lessonEndTime,
                studentName: schedule.studentName || schedule.userName || t('busy_time_label'),
                type: schedule.scheduleType || 'regular'
              });
            });
          } else if (dayEvent.startTime && dayEvent.endTime) {
            // 단일 이벤트 구조인 경우
            if (userInfo && 'id' in userInfo && dayEvent.userId === (userInfo as any).id) {
              return;
            }
            busyTimes.push({
              startTime: dayEvent.startTime,
              endTime: dayEvent.endTime,
              studentName: dayEvent.studentName || dayEvent.userName || t('busy_time_label'),
              type: dayEvent.scheduleType || 'regular'
            });
          }
        });

        setTeacherBusyTimes(busyTimes);
      } catch {
        setTeacherBusyTimes([]);
      }
    } catch (error) {
      setTeacherBusyTimes([] as any);
    }
  };

  // 요일 변경 시 시간 초기화만 수행 (API 호출은 customEndDate 변경 시에만)
  useEffect(() => {
    // 사용자 정규 요일 반영 전에는 호출 지연
    if (!didInitFromUser && userInfo && 'lessonDayOfWeek' in (userInfo as any)) {
      return;
    }

    // 요일이 변경되면 시간 초기화만 수행
    if (customEndDate) {
      setRequestedTime('11:00');
    }
  }, [selectedWeekDay, didInitFromUser, customEndDate]);

  // 사용자 정규 요일을 기본 선택 요일로 동기화
  useEffect(() => {
    if (userInfo && 'lessonDayOfWeek' in (userInfo as any)) {
      setSelectedWeekDay((userInfo as any).lessonDayOfWeek || 'monday');
    }
  }, [userInfo]);

  // 날짜 지정 모드에서 변경 요청 날짜를 선택하면 해당 요일로 동기화하고 API 호출
  useEffect(() => {
    const checkHolidayAndFetch = async () => {
      if (customEndDate) {
        // 휴일 체크
        try {
          const holidayResponse = await holidayAPI.checkHoliday(customEndDate);
          if (holidayResponse.data?.success && holidayResponse.data.data?.isHoliday) {
            const holidayTitle = holidayResponse.data.data.holiday?.title || t('academy_holiday');
            setHolidayError(t('holiday_schedule_unavailable', { title: holidayTitle }));
            return; // 휴일이면 바쁜 시간 조회하지 않음
          } else {
            setHolidayError(null);
          }
        } catch {
          setHolidayError(null);
        }

        const wd = getWeekdayFromDate(customEndDate);
        if (wd) setSelectedWeekDay(wd);
        // 날짜가 변경되면 즉시 API 호출 (요일 변경과 별개로)
        fetchTeacherBusyTimes();
      }
    };
    checkHolidayAndFetch();
  }, [customEndDate]);

  // 정기 일정 변경 모드에서 요일 변경 시 바쁜 시간 조회
  useEffect(() => {
    if (changeMode === 'permanent' && newWeekDay) {
      fetchPermanentBusyTimes(newWeekDay);
    }
  }, [changeMode, newWeekDay]);

  // 사용자의 수업 일정 오버라이드 로드
  useEffect(() => {
    const loadUserOverrides = async () => {
      try {
        if (!userInfo || !('id' in userInfo) || !('teacherId' in userInfo)) return;

        // 최근 6개월간의 변경/취소 일정 조회
        const today = new Date();
        const sixMonthsAgo = new Date(today);
        sixMonthsAgo.setMonth(today.getMonth() - 6);
        const sixMonthsLater = new Date(today);
        sixMonthsLater.setMonth(today.getMonth() + 6);

        const response = await calendarAPI.getUnifiedCalendar({
          startDate: sixMonthsAgo.toISOString().split('T')[0],
          endDate: sixMonthsLater.toISOString().split('T')[0],
          teacherId: (userInfo as any).teacherId,
          userId: (userInfo as any).id,
          includeRegular: false, // 정규 수업은 제외
          includeModifications: true
        });

        if (response.data && response.data.success) {
          const eventsByDate = response.data.data.events || [];

          // 변경/취소 일정만 추출
          const overrides: any[] = [];
          eventsByDate.forEach((dayEvent: any) => {
            if (dayEvent.schedules && Array.isArray(dayEvent.schedules)) {
              dayEvent.schedules.forEach((schedule: any) => {
                if (schedule.scheduleType === 'rescheduled' || schedule.scheduleType === 'cancelled') {
                  // 취소/변경된 원본 날짜 (originalDate가 가장 정확, 없으면 dayEvent.date 사용)
                  const originalDate = schedule.originalDate || schedule.originalLessonDate || dayEvent.date;
                  overrides.push({
                    startDate: originalDate,
                    endDate: schedule.effectiveUntil || dayEvent.date,
                    newDate: schedule.scheduleDate || dayEvent.date,
                    scope: 'single',
                    type: schedule.scheduleType
                  });
                }
              });
            }
          });

          setUserOverrides(overrides);
        }
      } catch {
        // 사용자 오버라이드 로드 실패
      }
    };

    if (userInfo) {
      loadUserOverrides();
    }
  }, [userInfo]);

  // 사용자 정규 수업 정보를 auth에서 반영
  useEffect(() => {
    if (userInfo && 'lessonDayOfWeek' in (userInfo as any)) {
      const ui: any = userInfo;
      const toHHmm = (t?: string) => (t ? t.substring(0, 5) : '');
      setUserScheduleInfo({
        dayOfWeek: ui.lessonDayOfWeek || 'monday',
        startTime: toHHmm(ui.lessonStartTime),
        endTime: toHHmm(ui.lessonEndTime)
      });
      // 기본 희망 요일도 현재 정규 요일로 맞춤
      setSelectedWeekDay(ui.lessonDayOfWeek || 'monday');
      setDidInitFromUser(true);
    }
  }, [userInfo]);

  const handleCancelRequest = () => {
    // 폼 초기화
    setCustomStartDate('');
    setCustomEndDate('');
    setRequestReason('');
    setRequestedTime('11:00');
    setNewStartTime('11:00');
    setTeacherBusyTimes([] as any);
    setPermanentBusyTimes([] as any);
    setHolidayError(null);
  };

  const submitChangeRequest = async () => {
    if (!requestReason.trim()) return;

    // 한 주 변경 모드인 경우 날짜 필수
    if (changeMode === 'weekly' && (!customStartDate || !customEndDate)) return;

    setSubmitting(true);

    try {
      // 기존 pending 요청이 있는지 확인
      const existingRequestsResponse = await modificationAPI.checkPendingRequests();

      if (existingRequestsResponse.success && existingRequestsResponse.data) {
        let existingRequests: any[] = [];

        if (Array.isArray(existingRequestsResponse.data)) {
          existingRequests = existingRequestsResponse.data;
        } else {
          const data = existingRequestsResponse.data as any;
          existingRequests = data.requests || data.modifications || [];
        }

        // 현재 요청하려는 시간과 겹치는 기존 요청이 있는지 확인 (한 주 변경 모드만)
        if (changeMode === 'weekly') {
          const hasOverlappingRequest = existingRequests.some(existingRequest => {
            // 같은 날짜인지 확인
            if (existingRequest.requestedDate && customEndDate) {
              if (existingRequest.requestedDate === customEndDate) {
                return true; // 같은 날짜면 겹침
              }
            }

            // 같은 시간대인지 확인
            if (existingRequest.requestedStartTime && requestedTime) {
              if (existingRequest.requestedStartTime === requestedTime) {
                return true; // 같은 시간이면 겹침
              }
            }

            return false;
          });

          if (hasOverlappingRequest) {
            alert(t('overlapping_request_error'));
            setSubmitting(false);
            return;
          }
        }
      }

      // API 요청 데이터 구성
      let requestData: {
        requestType: string;
        changeType: string;
        changePeriod?: string;
        changeStartDate?: string;
        changeEndDate?: string;
        requestMessage: string;
        requestedDate?: string;
        requestedStartTime?: string | null;
        requestedEndTime?: string | null;
        requestedDayOfWeek?: string;
        lessonScheduleId?: number | string | null;
      };

      if (changeMode === 'permanent') {
        // 정기 일정 변경 요청 (종료 시간은 시작 시간 + 1시간)
        const computedEndTime = `${(parseInt(newStartTime.split(':')[0]) + 1).toString().padStart(2, '0')}:00`;
        requestData = {
          requestType: 'change_time', // 정기 일정 변경
          changeType: 'permanent',
          requestMessage: requestReason,
          requestedDayOfWeek: newWeekDay,
          requestedStartTime: newStartTime,
          requestedEndTime: computedEndTime,
          lessonScheduleId: selectedSchedule?.id || selectedSchedule?.scheduleId || null
        };
      } else {
        // 한 주 임시 변경 요청
        requestData = {
          requestType: 'reschedule', // 수업 일정 변경 요청
          changeType: 'temporary',
          changePeriod: 'custom',
          changeStartDate: customStartDate,
          changeEndDate: customEndDate,
          requestMessage: requestReason,
          requestedDate: customEndDate,
          requestedStartTime: requestedTime,
          requestedEndTime: requestedTime ? `${(parseInt(requestedTime.split(':')[0]) + 1).toString().padStart(2, '0')}:00` : undefined,
          lessonScheduleId: selectedSchedule?.id || selectedSchedule?.scheduleId || null
        };
      }



      // API 호출
      const response = await modificationAPI.createModificationRequest(requestData);

      if (response.success) {
        // 성공 후 폼 초기화
        handleCancelRequest();
        loadMyRequests();
        alert(t('change_request_submitted'));
      } else {
        alert(`${t('change_request_failed')}: ${response.message}`);
      }
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      let errorMessage = t('change_request_error');
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="user-lesson-schedule-page">
        <TeacherPageHeader title="수업 일정 변경 신청" />
        <div className="page-content">
          <div className="container">
            <div className="form-container">
              <div className="form-section">
                <div className="card-title">새 변경 신청</div>

                {/* 현재 정규 일정 표시 */}
                {userScheduleInfo && (
                  <div className="current-schedule-compact">
                    <span className="label">{t('current_regular_lesson_label')}</span>
                    <span className="value">
                      {userScheduleInfo.dayOfWeek ? getDayLabel(userScheduleInfo.dayOfWeek) : ''} {userScheduleInfo.startTime}~{userScheduleInfo.endTime}
                    </span>
                  </div>
                )}

                {/* 탭 컴포넌트 */}
                <Tabs
                  options={[
                    { value: 'weekly', label: t('one_week_change') },
                    { value: 'permanent', label: t('permanent_schedule_change') }
                  ]}
                  value={changeMode}
                  onChange={(value) => setChangeMode(value as 'weekly' | 'permanent')}
                />

                <div className="notice-text">
                  {changeMode === 'weekly'
                    ? t('same_day_modification_notice')
                    : t('permanent_modification_notice')}
                </div>

                <form onSubmit={(e) => { e.preventDefault(); submitChangeRequest(); }}>
                  {changeMode === 'weekly' ? (
                    // 한 주 변경 폼
                    <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="customStartDate" className="form-label">{t('change_target_date')}</label>
                      <CustomSelect
                        id="customStartDate"
                        options={(() => {
                          const dates: { value: string; label: string }[] = [];
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const tomorrow = new Date(today);
                          tomorrow.setDate(today.getDate() + 1);
                          const userDayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(userScheduleInfo?.dayOfWeek || 'monday');

                          // 먼저 정규 수업 날짜들을 생성 (원본 요일 기준)
                          for (let week = 0; week < 4; week++) {
                            for (let day = 0; day < 7; day++) {
                              const date = new Date(today);
                              date.setDate(today.getDate() + (week * 7) + day);

                              // 당일 제외: 내일 이후부터만 선택 가능
                              if (date.getDay() === userDayIndex && date >= tomorrow) {
                                const dateString = format(date, 'yyyy-MM-dd');

                                // 해당 날짜가 취소되었거나 다른 날짜로 변경되었는지 확인
                                const isOriginalDateRemoved = userOverrides.some(override => {
                                  if (override.scope === 'single') {
                                    // 원본 날짜(startDate)가 현재 날짜와 같으면 제외
                                    // (취소 또는 변경된 경우 모두 원본 날짜는 제외)
                                    // 날짜 형식 정규화 (YYYY-MM-DD 형식으로 비교)
                                    const overrideDate = override.startDate?.split('T')[0];
                                    return overrideDate === dateString;
                                  }
                                  return false;
                                });

                                if (!isOriginalDateRemoved) {
                                  const displayText = format(date, language === 'ENG' ? 'MMM d, yyyy (E)' : 'yyyy년 M월 d일 (E)', { locale: dateLocale });
                                  dates.push({ value: dateString, label: displayText });
                                }
                              }
                            }
                          }

                          // 변경된 수업 날짜들을 추가 (rescheduled인 경우만)
                          userOverrides.forEach(override => {
                            if (override.type === 'rescheduled' && override.newDate) {
                              const newDateString = override.newDate?.split('T')[0];
                              const newDate = new Date(newDateString);

                              // 4주 이내이고 내일 이후인 경우만 추가
                              const fourWeeksLater = new Date(today);
                              fourWeeksLater.setDate(today.getDate() + 28);

                              if (newDate >= tomorrow && newDate <= fourWeeksLater) {
                                // 이 날짜가 이미 취소되었는지 확인
                                const isCancelled = userOverrides.some(o =>
                                  o.type === 'cancelled' && o.startDate?.split('T')[0] === newDateString
                                );

                                if (!isCancelled) {
                                  // 이미 dates에 있는지 확인 (중복 방지)
                                  const alreadyExists = dates.some(d => d.value === newDateString);
                                  if (!alreadyExists) {
                                    const displayText = format(newDate, language === 'ENG' ? 'MMM d, yyyy (E)' : 'yyyy년 M월 d일 (E)', { locale: dateLocale });
                                    dates.push({ value: newDateString, label: displayText });
                                  }
                                }
                              }
                            }
                          });

                          // 날짜순으로 정렬
                          dates.sort((a, b) => a.value.localeCompare(b.value));

                          return dates;
                        })()}
                        value={customStartDate}
                        onChange={setCustomStartDate}
                        placeholder={t('select_date_placeholder')}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="customEndDate" className="form-label">{t('requested_change_date')}</label>
                      <input
                        type="date"
                        id="customEndDate"
                        className={`form-input ${holidayError ? 'error' : ''}`}
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        min={(() => {
                          const tomorrow = new Date();
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          return format(tomorrow, 'yyyy-MM-dd');
                        })()}
                        required
                      />
                      {holidayError && (
                        <small className="form-error">{holidayError}</small>
                      )}
                    </div>
                  </div>

                  {holidayError ? (
                    <div className="holiday-notice-box">
                      <span className="holiday-icon">🏖️</span>
                      <p className="holiday-message">{holidayError}</p>
                    </div>
                  ) : (
                    <>
                      <div className="form-group">
                        <label htmlFor="requestedTime" className="form-label">{t('select_time')}</label>
                        <div className="time-slots">
                          {TIME_SLOTS
                            .filter(time => !isLastSlot(time))
                            .map(time => {
                              const isBusy = isTimeSlotBusy(time, teacherBusyTimes);
                              const canMerge = canMergeWithNext(time, teacherBusyTimes);
                              const isDisabled = isBusy || !canMerge;
                              const isSelected = requestedTime === time;

                              // 종료 시간 계산 (1시간 후)
                              const [hour, min] = time.split(':').map(Number);
                              const endTime = `${String(hour + 1).padStart(2, '0')}:${String(min).padStart(2, '0')}`;

                              return (
                                <button
                                  key={time}
                                  type="button"
                                  className={`time-slot ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                  onClick={() => !isDisabled && handleTimeSelect(time)}
                                  disabled={isDisabled}
                                  title={isBusy ? t('time_already_reserved') : !canMerge ? t('one_hour_lesson_unavailable') : `${time} - ${endTime}`}
                                >
                                  {time} - {endTime}
                                </button>
                              );
                            })}
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="requestReason" className="form-label">{t('reason_for_change')}</label>
                        <textarea
                          id="requestReason"
                          className="form-textarea"
                          value={requestReason}
                          onChange={(e) => setRequestReason(e.target.value)}
                          placeholder={t('reason_placeholder')}
                          required
                          rows={3}
                        />
                      </div>

                      {/* 변경 요약 미리보기 */}
                      {customStartDate && customEndDate && requestedTime && (
                        <div className="change-preview">
                          <div className="preview-item">
                            <span className="preview-label">{t('change_date_label')}</span>
                            <span className="preview-value">
                              {format(new Date(customStartDate), 'M/d', { locale: dateLocale })} → {format(new Date(customEndDate), 'M/d (E)', { locale: dateLocale })}
                            </span>
                          </div>
                          <div className="preview-item">
                            <span className="preview-label">{t('change_time_label')}</span>
                            <span className="preview-value">
                              {userScheduleInfo?.startTime} → {requestedTime}
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="form-actions">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={submitting || !requestReason.trim() || !requestedTime || !customStartDate || !customEndDate}
                        >
                          {submitting ? t('submitting') : t('submit_request')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleCancelRequest}
                        >
                          {t('reset')}
                        </button>
                      </div>
                    </>
                  )}
                  </>
                  ) : (
                    // 정기 일정 변경 폼
                    <>
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="newWeekDay" className="form-label">{t('change_weekday_label')}</label>
                          <CustomSelect
                            id="newWeekDay"
                            options={[
                              { value: 'monday', label: getDayLabel('monday') },
                              { value: 'tuesday', label: getDayLabel('tuesday') },
                              { value: 'wednesday', label: getDayLabel('wednesday') },
                              { value: 'thursday', label: getDayLabel('thursday') },
                              { value: 'friday', label: getDayLabel('friday') },
                              { value: 'saturday', label: getDayLabel('saturday') },
                              { value: 'sunday', label: getDayLabel('sunday') }
                            ]}
                            value={newWeekDay}
                            onChange={setNewWeekDay}
                            placeholder={t('select_weekday')}
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="newStartTime" className="form-label">{t('select_time')}</label>
                        <div className="time-slots">
                          {TIME_SLOTS
                            .filter(time => !isLastSlot(time))
                            .map(time => {
                              const isBusy = isTimeSlotBusy(time, permanentBusyTimes);
                              const canMerge = canMergeWithNext(time, permanentBusyTimes);
                              const isDisabled = isBusy || !canMerge;
                              const isSelected = newStartTime === time;

                              // 종료 시간 계산 (1시간 후)
                              const [hour, min] = time.split(':').map(Number);
                              const endTime = `${String(hour + 1).padStart(2, '0')}:${String(min).padStart(2, '0')}`;

                              return (
                                <button
                                  key={time}
                                  type="button"
                                  className={`time-slot ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                  onClick={() => !isDisabled && handlePermanentTimeSelect(time)}
                                  disabled={isDisabled}
                                  title={isBusy ? t('time_already_reserved') : !canMerge ? t('one_hour_lesson_unavailable') : `${time} - ${endTime}`}
                                >
                                  {time} - {endTime}
                                </button>
                              );
                            })}
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="requestReason" className="form-label">{t('reason_for_change')}</label>
                        <textarea
                          id="requestReason"
                          className="form-textarea"
                          value={requestReason}
                          onChange={(e) => setRequestReason(e.target.value)}
                          placeholder={t('reason_placeholder')}
                          required
                          rows={3}
                        />
                      </div>

                      {/* 정기 변경 미리보기 */}
                      {newWeekDay && newStartTime && (
                        <div className="change-preview">
                          <div className="preview-item">
                            <span className="preview-label">{t('current_schedule_label')}</span>
                            <span className="preview-value">
                              {userScheduleInfo?.dayOfWeek ? getDayLabel(userScheduleInfo.dayOfWeek) : ''} {userScheduleInfo?.startTime}~{userScheduleInfo?.endTime}
                            </span>
                          </div>
                          <div className="preview-item">
                            <span className="preview-label">{t('after_change_label')}</span>
                            <span className="preview-value">
                              {getDayLabel(newWeekDay)} {newStartTime}~{`${(parseInt(newStartTime.split(':')[0]) + 1).toString().padStart(2, '0')}:00`}
                            </span>
                          </div>
                        </div>
                      )}

                      <div className="form-actions">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={submitting || !requestReason.trim() || !newWeekDay || !newStartTime}
                        >
                          {submitting ? t('submitting') : t('submit_request')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleCancelRequest}
                        >
                          {t('reset')}
                        </button>
                      </div>
                    </>
                  )}
                </form>
              </div>

              {/* 신청 내역 (시안: 우측 패널) */}
              <div className="history-card">
                <div className="history-head">신청 내역</div>
                <div className="history-grid history-grid-head">
                  <div>기존 수업</div>
                  <div>변경 희망</div>
                  <div>신청일</div>
                  <div>상태</div>
                </div>
                {myRequests.length === 0 ? (
                  <div className="history-empty">신청 내역이 없습니다</div>
                ) : (
                  myRequests.map((req) => (
                    <div className="history-grid history-row" key={req.id}>
                      <div>
                        {req.changeStartDate
                          ? format(new Date(req.changeStartDate), 'M. d (E)', { locale: dateLocale })
                          : '—'}
                      </div>
                      <div className="to-cell">
                        {req.requestedDate
                          ? format(new Date(req.requestedDate), 'M. d (E)', { locale: dateLocale })
                          : req.requestedWeekDay || ''}
                        {req.requestedStartTime && ` ${req.requestedStartTime.slice(0, 5)}`}
                      </div>
                      <div className="date-cell">
                        {req.createdAt ? format(new Date(req.createdAt), 'M. d') : ''}
                      </div>
                      <div>
                        <span className={`status-badge ${req.status}`}>
                          {req.status === 'pending' ? '대기' : req.status === 'approved' ? '승인' : req.status === 'rejected' ? '거절' : '취소'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserLessonSchedule;